import {
  ActuatorModalityType,
  ConfigurationType,
  Device,
  RoomObjectDTO,
  Slot,
  SlotStatus,
  ThermoActuatorType,
  RoomObjectType,
  ComponentType,
  DeviceDTO,
  LightType,
  FunctionType,
} from '@/../client-generator/generated-clients/plants';
import i18n from '../../translations/i18n';
import MxConstants from './mx-constants';
import SlotsConfigService from './slots-assign-rules-config';
import {
  SlotRuleServiceType,
  WorkflowCallbackData,
  WorkflowCallbackFunction,
} from './slots-assign-rules-interfaces';
import BaseSlotRuleService from './slots-assign-rules-service';

export default class AssignAutomationCommandsSlotRuleService extends BaseSlotRuleService {
  _adjacentSlotVm: Slot | null = null;

  constructor(
    roomObject: RoomObjectDTO,
    workflowCallback: WorkflowCallbackFunction
  ) {
    super(
      roomObject,
      -1,
      ThermoActuatorType.None,
      ActuatorModalityType.None,
      workflowCallback
    );
    this.slotRuleServiceType = SlotRuleServiceType.AUTOMATION_COMMAND;

    this._noOfAdjacent = this.OneAdjacent;
  }

  onSlotMessage(device: DeviceDTO, slot: Slot) {
    const is8BtnDeviceOrCrystalDeviceWithPairSelection = SlotsConfigService.is8BtnDeviceOrCrystalDeviceWithPairSelection(
      device
    );

    switch (slot.status) {
      case SlotStatus.Configured:
        {
          if (this.isSlotLinkedWithCurrentRoomObject(slot)) {
            const data: WorkflowCallbackData = {
              warningTitle: `${i18n.global.t(
                'AppResource.UnlinkThe'
              )} ${i18n.global.t('AppResource.Module')}`,
              warningMessage: `${i18n.global.t(
                'AppResource.UnlinkMessage'
              )} ${i18n.global.t('AppResource.Module')} ${
                slot.index
              } ${i18n.global.t('AppResource.From')} ${this.roomObject.name}`,
              _adjacentSlotVm: device.slots?.filter(
                (sl) =>
                  this.slotHasAdjacentSlots(slot, sl) &&
                  sl.assignedComponentType == ComponentType.Actuator
              )[0],
              _isUnlinkFlow: true,
              isWarningRequested: true,
              isConfirmation: true,
            };
            this._workflowCallback(
              data,
              () => this.doYesCommand(data, device, slot),
              () => this.doNoCommand(data, device, slot)
            );
          }
        }
        break;
      case SlotStatus.NotConfigured:
        {
          if (
            SlotsConfigService.automationCommandsSpecialDevices(device) &&
            (this.roomObject.type == RoomObjectType.Shutter ||
              this.roomObject.type == RoomObjectType.Blind ||
              this.roomObject.lightType == LightType.Dimmer)
          ) {
            this._noOfAdjacent = this.ThreeAdjacent;
            this.markOrUnmarkSlot(
              this.roomObject,
              slot,
              device,
              SlotStatus.NotConfigured,
              SlotStatus.MarkedForLinking
            );
          } else if (
            (this.roomObject.type == RoomObjectType.Shutter &&
              is8BtnDeviceOrCrystalDeviceWithPairSelection) ||
            (this.roomObject.type == RoomObjectType.Blind &&
              is8BtnDeviceOrCrystalDeviceWithPairSelection)
          ) {
            this._noOfAdjacent = this.TwoAdjacent;
            this.markOrUnmarkSlot(
              this.roomObject,
              slot,
              device,
              SlotStatus.NotConfigured,
              SlotStatus.MarkedForLinking,
              false,
              true
            );
          } else {
            this._noOfAdjacent = this.OneAdjacent;

            const tmp = device.slots?.filter(
              (sl) =>
                this.slotHasAdjacentSlots(slot, sl) &&
                sl.assignedComponentType == ComponentType.Command
            );

            this._adjacentSlotVm = tmp ? tmp[tmp.length - 1] : null;

            const isDevice15117orDevice15121orDevice15119 = SlotsConfigService.isDevice15117orDevice15121orDevice15119(
              device
            );

            // MXSRV-1841
            const isDevice3477 = SlotsConfigService.isDevice3477(device);

            // MXSRV-1875
            const isDeviceF428 = SlotsConfigService.isDeviceF428(device);

            //MXSRV-1876
            const isDeviceCrystal8Scene = SlotsConfigService.isDeviceCrystal8Scene(
              device
            );

            if (
              this._adjacentSlotVm != null &&
              !isDevice15117orDevice15121orDevice15119 &&
              !isDevice3477 &&
              !isDeviceF428 &&
              !isDeviceCrystal8Scene
            ) {
              const data: WorkflowCallbackData = {
                warningTitle: i18n.global.t(
                  'AppResource.MostrineQuestionTitle'
                ),
                warningMessage: i18n.global.t('AppResource.MostrineQuestion'),
                _isMostrineFlow: true,
                isWarningRequested: true,
                isConfirmation: true,
              };
              this._workflowCallback(
                data,
                () => this.doYesCommand(data, device, slot),
                () => this.doNoCommand(data, device, slot)
              );
            } else {
              if (this.isSpecialDeviceF428(slot, device)) {
                //LR if true, there is only one slot left in device with model: F428
                const adjacentSlotVm = device.slots?.filter(
                  (sl) =>
                    sl.status == SlotStatus.NotConfigured &&
                    (sl.index == (slot.index ?? 0) + 1 ||
                      sl.index == (slot.index ?? 0) - 1)
                )[0];
                if (adjacentSlotVm != null) {
                  this.addOrUpdateSlot(slot);
                  this.addOrUpdateSlot(adjacentSlotVm);
                  this.updateConfigurationType(
                    slot,
                    adjacentSlotVm,
                    ConfigurationType.None
                  );
                } else {
                  const data: WorkflowCallbackData = {
                    warningTitle: i18n.global.t(
                      'AppResource.NotEnoughAdjacentChannelsTitle'
                    ),
                    warningMessage: i18n.global.t(
                      'AppResource.NotEnoughAdjacentChannelsMessage'
                    ),
                    isConfirmation: false,
                    isWarningRequested: true,
                  };
                  this._workflowCallback(
                    data,
                    () => this.doYesCommand(data, device, slot),
                    () => this.doNoCommand(data, device, slot),
                    () => this.doAlertCommand(data, device, slot)
                  );
                }
              } else {
                this.addOrUpdateSlot(slot);
              }
            }
          }
        }
        break;
      case SlotStatus.MarkedForLinking:
        {
          if (
            SlotsConfigService.automationCommandsSpecialDevices(device) &&
            (this.roomObject.type == RoomObjectType.Shutter ||
              this.roomObject.type == RoomObjectType.Blind ||
              this.roomObject.lightType == LightType.Dimmer)
          ) {
            this._noOfAdjacent = this.ThreeAdjacent;
            this.markOrUnmarkSlot(
              this.roomObject,
              slot,
              device,
              SlotStatus.MarkedForLinking,
              SlotStatus.NotConfigured
            );
          } else if (
            (this.roomObject.type == RoomObjectType.Shutter &&
              is8BtnDeviceOrCrystalDeviceWithPairSelection) ||
            (this.roomObject.type == RoomObjectType.Blind &&
              is8BtnDeviceOrCrystalDeviceWithPairSelection)
          ) {
            this._noOfAdjacent = this.TwoAdjacent;
            this.markOrUnmarkSlot(
              this.roomObject,
              slot,
              device,
              SlotStatus.MarkedForLinking,
              SlotStatus.NotConfigured
            );
          } else {
            this._noOfAdjacent = this.OneAdjacent;
            slot.status = SlotStatus.NotConfigured;
            this._adjacentSlotVm =
              device.slots?.filter((sl) =>
                this.slotHasAdjacentSlots(slot, sl)
              )[0] ?? null;
            if (slot.configurationType != ConfigurationType.None) {
              this.updateConfigurationType(
                slot,
                this._adjacentSlotVm,
                ConfigurationType.None
              );
            }
            this.removeOrUpdateSlot(slot);
            if (
              this.isSpecialDeviceF428(slot, device) &&
              this._adjacentSlotVm
            ) {
              this.removeOrUpdateSlot(this._adjacentSlotVm);
            }
          }
        }
        break;
      case SlotStatus.NotUsed:
      default:
        break;
    }
  }

  isSpecialDeviceF428(slot: Slot, device: Device) {
    if (slot != null && this.roomObject?.type == RoomObjectType.Shutter) {
      if (device != null && device.model == MxConstants.F428) {
        return true;
      }
    }

    return false;
  }

  updateConfigurationType(
    selectedSlotViewModel: Slot | null,
    adjacentSlotViewModel: Slot | null,
    configurationType: ConfigurationType
  ) {
    if (selectedSlotViewModel != null) {
      selectedSlotViewModel.configurationType = configurationType;
    }
    if (adjacentSlotViewModel != null) {
      adjacentSlotViewModel.configurationType = configurationType;
    }
  }

  areSlotsCompatibleForPresetManagement(device: DeviceDTO) {
    if (this.roomObject?.devices != null && device != null) {
      const isShutterRoomObject =
        this.roomObject.type == RoomObjectType.Shutter;
      const isActuatorModelCorrect = this.roomObject.devices.some((d) =>
        this.isActuatorModelCorrect(d)
      );
      const isLastComponentCollapsed = !!device.model;
      const commandCanHandlePreset = this.hasCommandThatCanHandlePreset(device);

      return (
        isShutterRoomObject &&
        isActuatorModelCorrect &&
        isLastComponentCollapsed &&
        commandCanHandlePreset
      );
    }
    return false;
  }

  isActuatorModelCorrect(d: DeviceDTO) {
    const deviceHasCorrectFunctionType =
      d.functionType == FunctionType.All ||
      d.functionType == FunctionType.Automation;
    const devicheHasModel = !!d.model;
    const deviceIsActuatorWithAbailableSlots =
      d.slots != null &&
      d.slots.some(
        (s) =>
          (s.status == SlotStatus.MarkedForLinking ||
            s.status == SlotStatus.Configured) &&
          (s.assignedComponentType == ComponentType.Actuator ||
            s.assignedComponentType == ComponentType.None)
      );

    return (
      deviceHasCorrectFunctionType &&
      devicheHasModel &&
      d.isShutterWithPosition &&
      deviceIsActuatorWithAbailableSlots
    );
  }

  hasCommandThatCanHandlePreset(device: DeviceDTO) {
    return device?.canHandlePreset ?? false;
  }

  doNoCommand(data: WorkflowCallbackData, device: DeviceDTO, slot: Slot) {
    super.doNoCommand(data, device, slot);
    if (data._isMostrineFlow) {
      if (this.areSlotsCompatibleForPresetManagement(device)) {
        const data: WorkflowCallbackData = {
          isPresetWarningRequested: true,
          _isMostrineFlow: false,
        };
        this._workflowCallback(
          data,
          () => this.doPresetYesCommand(data, device, slot),
          () => this.doPresetNoCommand(data, device, slot)
        );
      } else {
        this.addOrUpdateSlot(slot);
      }
    }
  }

  doYesCommand(data: WorkflowCallbackData, device: DeviceDTO, slot: Slot) {
    if (slot != null) {
      if (data._isUnlinkFlow) {
        if (
          slot.configurationType != ConfigurationType.None &&
          this._adjacentSlotVm != null
        ) {
          this.updateConfigurationType(
            slot,
            this._adjacentSlotVm,
            ConfigurationType.None
          );
        }
        this.removeOrUpdateSlot(slot);
        if (this.isSpecialDeviceF428(slot, device) && this._adjacentSlotVm) {
          this.removeOrUpdateSlot(this._adjacentSlotVm);
        }
      } else if (data._isMostrineFlow) {
        this.addOrUpdateSlot(slot);
        this.updateConfigurationType(
          slot,
          this._adjacentSlotVm,
          ConfigurationType.Mostrine
        );
      }
    }
  }

  doPresetNoCommand(data: WorkflowCallbackData, device: DeviceDTO, slot: Slot) {
    this.addOrUpdateSlot(slot);
  }

  doPresetYesCommand(
    data: WorkflowCallbackData,
    device: DeviceDTO,
    slot: Slot
  ) {
    this.addOrUpdateSlot(slot);
    this.updateConfigurationType(
      slot,
      this._adjacentSlotVm,
      ConfigurationType.Preset
    );
  }
}
